import { z } from 'zod';
import { getDependentFieldOptionalSchema, getDependentFieldSchema, getUrlSchema } from '../../../constants/sharedSchemas';

const activitySchema = z.object({
  type: z.string().min(1, 'Activity is required'),
  description: z.string().min(1, 'Activity description is required'),
});
const activitySchemaOptional = z.object({
  type: z.string().optional(),
  description: z.string().optional(),
});

export const biographySchema = {
  required: z.object({
    general: z.object({
      participatePreviousExchange: z.string().min(1, 'This field is required'),
      willingToPayFees: getDependentFieldSchema('range', 'yes'),
    }),

    activities: z.object({
      activity1: activitySchema,
      activity2: activitySchema,
      activity3: activitySchema,
    }),

    bio: z.object({
      studentBio: z.string().min(1, 'Bio is required'),
      videoIntroductionLink: getUrlSchema('Video introduction link is required'),
    }),

    awards: z.array(
      z.object({
        name: z.string().min(1, 'Award name is required'),
        institutionName: z.string().min(1, 'Institution name is required'),
        reason: z.string().min(1, 'Award reason is required'),
        date: z.date({ required_error: 'Award date is required' }),
      }),
    ),
  }),
  optional: z.object({
    general: z.object({
      participatePreviousExchange: z.string().optional(),
      willingToPayFees: getDependentFieldOptionalSchema('range'),
    }),

    activities: z.object({
      activity1: activitySchemaOptional,
      activity2: activitySchemaOptional,
      activity3: activitySchemaOptional,
    }),

    bio: z.object({
      studentBio: z.string().optional(),
      videoIntroductionLink: z.string().url('URL address is invalid').optional(),
    }),

    awards: z
      .array(
        z.object({
          name: z.string().optional(),
          institutionName: z.string().optional(),
          reason: z.string().optional(),
          date: z.date().optional(),
        }),
      )
      .optional(),
  }),
};

export const awardDefaultValues = { name: '', institutionName: '', reason: '', date: undefined };

export type BiographyFormValues = z.infer<(typeof biographySchema)['required']> | z.infer<(typeof biographySchema)['optional']>;

export const defaultValues: BiographyFormValues = {
  general: {
    participatePreviousExchange: '',
    willingToPayFees: {
      value: '',
      range: '',
    },
  },

  activities: {
    activity1: { type: '', description: '' },
    activity2: { type: '', description: '' },
    activity3: { type: '', description: '' },
  },

  bio: {
    studentBio: '',
    videoIntroductionLink: undefined,
  },

  awards: [awardDefaultValues],
};
